/** The receipt's Deliveries link for a booking save: the deliveries of exactly the events
 *  the save emitted, from the distinct `events[].tag` values the apply returned.
 *
 *  - `console:booking_edit[<audit uuid>]` is unique to the save — the tag alone pins its
 *    deliveries.
 *  - A booking that took only actions tags its events with the action's own tag
 *    (`console:checkin`, `console:precheckin`), which every such action on any booking
 *    carries. Those are scoped to the bookings the events concern (`participant` = the
 *    console booking pk — never `booking=`, the booking-system id) and to the time from
 *    the save on, so an earlier check-in of the same booking does not show.
 *
 *  Several tags read as one OR query. Null when the save emitted nothing. */
import type { RecordApplyResult } from '@/features/records/editor/types';
import { allOf, anyOf, cond, deliveryLogsHref, sinceCond } from '@/utils/gridLinks';

/** How long after the save a shared-tag delivery still counts as this save's. */
export const SHARED_TAG_WINDOW_MS = 24 * 3_600_000;

export function saveDeliveriesHref(
  result: Pick<RecordApplyResult, 'audit_uuid' | 'event_tag' | 'events' | 'saved_ts'>,
): string | null {
  if (result.events.length === 0) return null;
  const byTag = new Map<string, Set<number>>();
  for (const e of result.events) {
    if (!e.tag) continue;
    const ids = byTag.get(e.tag) ?? new Set<number>();
    if (e.entity === 'booking') ids.add(e.id);
    byTag.set(e.tag, ids);
  }
  if (byTag.size === 0) return result.event_tag ? deliveryLogsHref([cond('tag', result.event_tag)]) : null;

  let shared = false;
  const clauses = [...byTag].map(([tag, ids]) => {
    if (result.audit_uuid && tag.includes(result.audit_uuid)) return cond('tag', tag);
    shared = true;
    const who = [...ids].sort((a, b) => a - b).map((id) => cond('participant', String(id), 'exact'));
    return who.length > 0 ? allOf([cond('tag', tag), anyOf(who)]) : cond('tag', tag);
  });
  return deliveryLogsHref([
    anyOf(clauses),
    ...(shared ? [sinceCond('created', result.saved_ts, SHARED_TAG_WINDOW_MS)] : []),
  ]);
}
